import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

// ── Sparkline SVG ──────────────────────────────────────────────────────────
function Sparkline({ data, color = '#00C896', height = 40, width = 80 }) {
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((v - min) / range) * (height - 6) - 3
    return `${x},${y}`
  }).join(' ')
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

// ── Donut Chart ────────────────────────────────────────────────────────────
function Donut({ segments }) {
  const r = 30; const cx = 36; const cy = 36; const circ = 2 * Math.PI * r
  let offset = 0
  return (
    <svg width={72} height={72} viewBox="0 0 72 72">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1A2540" strokeWidth={10} />
      {segments.map((s, i) => {
        const dash = (s.pct / 100) * circ
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth={10}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset * circ / 100 + circ * 0.25}
            style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
          />
        )
        offset += s.pct
        return el
      })}
    </svg>
  )
}

// ── Phone Frame ────────────────────────────────────────────────────────────
function PhoneFrame({ children, label, accent = '#00C896' }) {
  return (
    <div className="flex flex-col items-center gap-3 flex-shrink-0">
      <div className="relative" style={{ width: 200, height: 400 }}>
        {/* outer shell */}
        <div className="absolute inset-0 rounded-[32px]" style={{ background: '#0F1626', border: '2px solid #1A2540', boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)' }} />
        {/* notch */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-4 rounded-full" style={{ background: '#080C14', zIndex: 10 }} />
        {/* screen */}
        <div className="absolute rounded-[28px] overflow-hidden" style={{ inset: 6, background: '#0A0E1A' }}>
          {children}
        </div>
        {/* accent glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-1 rounded-full opacity-60" style={{ background: accent, filter: 'blur(6px)' }} />
      </div>
      <span className="text-xs font-mono tracking-widest uppercase" style={{ color: '#8899BB', fontFamily: 'var(--font-mono)' }}>{label}</span>
    </div>
  )
}

// ── SCREEN: Splash ─────────────────────────────────────────────────────────
function ScreenSplash() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative" style={{ background: 'linear-gradient(160deg, #0A0E1A 0%, #0F1F3A 100%)' }}>
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 50% 40%, #00C896 0%, transparent 60%)' }} />
      <div className="relative text-center px-6">
        <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: '#00C896', boxShadow: '0 8px 32px rgba(0,200,150,0.4)' }}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path d="M4 20 L10 12 L16 16 L24 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="24" cy="6" r="2" fill="white"/>
          </svg>
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: '#F7F2E8', letterSpacing: '-0.5px' }}>Vestly</div>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: '#8899BB', letterSpacing: '0.15em', marginTop: 4, textTransform: 'uppercase' }}>Smart Investing</div>
        <div className="mt-10 text-xs" style={{ color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}>Loading portfolio...</div>
        <div className="mt-2 w-20 h-0.5 mx-auto rounded-full overflow-hidden" style={{ background: '#1A2540' }}>
          <div className="h-full rounded-full w-3/5" style={{ background: '#00C896' }} />
        </div>
      </div>
    </div>
  )
}

// ── SCREEN: Onboarding 1 ───────────────────────────────────────────────────
function ScreenOnboarding1() {
  return (
    <div className="w-full h-full flex flex-col" style={{ background: '#080C14', padding: '40px 18px 24px' }}>
      <div className="flex-1 flex flex-col justify-center">
        <div className="w-10 h-10 rounded-xl mb-5 flex items-center justify-center" style={{ background: 'rgba(0,200,150,0.12)', border: '1px solid rgba(0,200,150,0.3)' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 2L12.5 7.5H18L13.5 11L15.5 17L10 13.5L4.5 17L6.5 11L2 7.5H7.5L10 2Z" fill="#00C896"/>
          </svg>
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: '#F7F2E8', lineHeight: 1.2, marginBottom: 10 }}>
          Your money,<br /><em>working harder</em>
        </div>
        <div style={{ fontSize: 11, color: '#8899BB', lineHeight: 1.6 }}>
          Vestly uses AI-powered insights to build and manage your portfolio automatically.
        </div>
        {/* <div className="mt-6 flex gap-2">
          {['Step 1', 'Step 2', 'Step 3'].map((s, i) => (
            <div key={s} className="flex-1 h-0.5 rounded-full" style={{ background: i === 0 ? '#00C896' : '#1A2540' }} />
          ))}
        </div> */}
      </div>
      <div className="w-full py-1.5 text-center rounded-xl text-sm font-medium" style={{ background: '#00C896', color: '#080C14', fontFamily: 'var(--font-body)' }}>
        Get Started
      </div>
      <div className="text-center mt-3" style={{ fontSize: 10, color: '#8899BB' }}>Already have an account? <span style={{ color: '#00C896' }}><br/>Log in</span></div>
    </div>
  )
}

// ── SCREEN: Onboarding 2 — Risk Profile ───────────────────────────────────
function ScreenOnboarding2() {
  const [selected, setSelected] = useState(1)
  const opts = [
    { label: 'Conservative', sub: '4–6% annual', icon: '🛡️' },
    { label: 'Balanced', sub: '8–12% annual', icon: '⚖️' },
    { label: 'Aggressive', sub: '15–20% annual', icon: '🚀' },
  ]
  return (
    <div className="w-full h-full flex flex-col" style={{ background: '#080C14', padding: '40px 16px 24px' }}>
      <div style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>Step 2 of 3</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 19, color: '#F7F2E8', marginBottom: 6 }}>Risk tolerance</div>
      <div style={{ fontSize: 10, color: '#8899BB', marginBottom: 16 }}>How comfortable are you with market fluctuations?</div>
      <div className="flex flex-col gap-2 flex-1">
        {opts.map((o, i) => (          <div key={o.label} role="button" onClick={() => setSelected(i)}
            className="p-3 rounded-xl cursor-pointer transition-all text-left"
            aria-pressed={selected === i}
            style={{
              background: selected === i ? 'rgba(0,200,150,0.08)' : '#0F1626',
              border: `1px solid ${selected === i ? '#00C896' : '#1A2540'}`,
            }}>
            <div className="flex items-center gap-2">
              <span style={{ fontSize: 16 }}>{o.icon}</span>
              <div>
                <div style={{ fontSize: 12, color: selected === i ? '#F7F2E8' : '#C2CEDF', fontWeight: 500 }}>{o.label}</div>
                <div style={{ fontSize: 9, color: '#8899BB', fontFamily: 'var(--font-mono)' }}>{o.sub}</div>
              </div>
              <div className="ml-auto w-4 h-4 rounded-full border flex items-center justify-center" style={{ borderColor: selected === i ? '#00C896' : '#1A2540' }}>
                {selected === i && <div className="w-2 h-2 rounded-full" style={{ background: '#00C896' }} />}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="w-full py-3 text-center rounded-xl text-sm font-medium mt-4" style={{ background: '#00C896', color: '#080C14' }}>Continue</div>
    </div>
  )
}

// ── SCREEN: Portfolio Dashboard ────────────────────────────────────────────
function ScreenDashboard() {
  return (
    <div className="w-full h-full flex flex-col overflow-y-auto" style={{ background: '#080C14', padding: '36px 14px 16px' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div style={{ fontSize: 10, color: '#8899BB' }}>Good morning,</div>
          <div style={{ fontSize: 14, color: '#F7F2E8', fontWeight: 600 }}>Alex Chen</div>
        </div>
        <div className="w-8 h-8 rounded-full overflow-hidden" style={{ background: '#1A2540', border: '1.5px solid #00C896' }}>
          <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: '#00C896' }}>AC</div>
        </div>
      </div>
      {/* Balance */}
      <div className="rounded-2xl p-4 mb-3" style={{ background: 'linear-gradient(135deg, #0F1F3A 0%, #0A1628 100%)', border: '1px solid #1A2540' }}>
        <div style={{ fontSize: 9, color: '#8899BB', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>Total Portfolio</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: '#F7F2E8', marginTop: 2 }}>$48,392.40</div>
        <div className="flex items-center gap-1.5 mt-1">
          <div className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896', fontFamily: 'var(--font-mono)', fontSize: 9 }}>▲ +2.84%</div>
          <span style={{ fontSize: 9, color: '#8899BB' }}>+$1,336 this month</span>
        </div>
        <Sparkline data={[32000, 33500, 31800, 35200, 36800, 35100, 38200, 40100, 42800, 44200, 46500, 48392]} width={168} height={44} />
      </div>
      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[
          { label: 'Invested', val: '$42,100', sub: 'Principal', col: '#C2CEDF' },
          { label: 'Returns', val: '+$6,292', sub: 'All time', col: '#00C896' },
        ].map(s => (
          <div key={s.label} className="rounded-xl p-3" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
            <div style={{ fontSize: 9, color: '#8899BB', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
            <div style={{ fontSize: 12, color: s.col, fontWeight: 600, marginTop: 2 }}>{s.val}</div>
            <div style={{ fontSize: 9, color: '#8899BB' }}>{s.sub}</div>
          </div>
        ))}
      </div>
      {/* Holdings */}
      <div style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 600, marginBottom: 8 }}>Holdings</div>
      {[
        { name: 'AAPL', full: 'Apple Inc.', val: '$12,400', chg: '+1.2%', up: true },
        { name: 'MSFT', full: 'Microsoft', val: '$9,820', chg: '+0.8%', up: true },
        { name: 'TSLA', full: 'Tesla', val: '$4,102', chg: '-2.1%', up: false },
      ].map(h => (
        <div key={h.name} className="flex items-center justify-between py-2.5" style={{ borderBottom: '1px solid #0F1626' }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#1A2540' }}>
              <span style={{ fontSize: 8, color: '#00C896', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{h.name[0]}</span>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 500 }}>{h.name}</div>
              <div style={{ fontSize: 8, color: '#8899BB' }}>{h.full}</div>
            </div>
          </div>
          <div className="text-right">
            <div style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 600 }}>{h.val}</div>
            <div style={{ fontSize: 8, color: h.up ? '#00C896' : '#FF6B7A', fontFamily: 'var(--font-mono)' }}>{h.chg}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── SCREEN: Investment Recommendations ─────────────────────────────────────
function ScreenRecommendations() {
  return (
    <div className="w-full h-full flex flex-col overflow-y-auto" style={{ background: '#080C14', padding: '36px 14px 16px' }}>
      <div className="flex items-center justify-between mb-4">
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: '#F7F2E8' }}>For You</div>
        <div style={{ fontSize: 9, color: '#00C896', fontFamily: 'var(--font-mono)' }}>AI Curated</div>
      </div>
      {/* Robo banner */}
      <div className="rounded-2xl p-3 mb-4" style={{ background: 'linear-gradient(120deg, rgba(0,200,150,0.12) 0%, rgba(0,168,125,0.06) 100%)', border: '1px solid rgba(0,200,150,0.25)' }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#00C896' }}>
            <span style={{ fontSize: 10 }}>🤖</span>
          </div>
          <div style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 600 }}>Robo-Advisor</div>
          <div className="ml-auto px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,200,150,0.2)', fontSize: 8, color: '#00C896', fontFamily: 'var(--font-mono)' }}>LIVE</div>
        </div>
        <div style={{ fontSize: 9, color: '#C2CEDF' }}>Based on your Balanced risk profile, we suggest rebalancing to increase tech sector exposure by 8%.</div>
      </div>
      {/* Cards */}
      {[
        { ticker: 'QQQ', name: 'NASDAQ ETF', type: 'ETF', match: 94, exp: '0.20%', ret: '+14.2%', trend: [100, 108, 105, 112, 110, 118, 116, 122, 120, 128] },
        { ticker: 'VTI', name: 'Total Market', type: 'ETF', match: 87, exp: '0.03%', ret: '+10.8%', trend: [100, 102, 101, 105, 107, 104, 109, 108, 112, 115] },
        { ticker: 'BRK.B', name: 'Berkshire B', type: 'Stock', match: 81, exp: '—', ret: '+9.1%', trend: [100, 103, 102, 106, 105, 108, 107, 110, 111, 114] },
      ].map(c => (
        <div key={c.ticker} className="rounded-xl p-3 mb-2" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="flex items-center gap-1.5">
                <span style={{ fontSize: 11, color: '#F7F2E8', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{c.ticker}</span>
                <span className="px-1 py-0.5 rounded text-xs" style={{ background: '#1A2540', color: '#8899BB', fontSize: 8 }}>{c.type}</span>
              </div>
              <div style={{ fontSize: 9, color: '#8899BB' }}>{c.name}</div>
            </div>
            <div className="text-right">
              <div style={{ fontSize: 9, color: '#00C896', fontFamily: 'var(--font-mono)' }}>{c.match}% match</div>
              <Sparkline data={c.trend} width={50} height={24} />
            </div>
          </div>
          <div className="flex gap-4">
            <div><div style={{ fontSize: 8, color: '#8899BB' }}>1yr Return</div><div style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)' }}>{c.ret}</div></div>
            <div><div style={{ fontSize: 8, color: '#8899BB' }}>Expense</div><div style={{ fontSize: 10, color: '#C2CEDF', fontFamily: 'var(--font-mono)' }}>{c.exp}</div></div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── SCREEN: Asset Detail ───────────────────────────────────────────────────
function ScreenAssetDetail() {
  const [tab, setTab] = useState(0)
  const tabs = ['1D', '1W', '1M', '1Y']
  const chartData = [
    [182, 184, 181, 185, 183, 187, 186, 188, 185, 190, 189, 192, 191, 194, 193],
    [175, 178, 176, 181, 179, 183, 182, 185, 184, 187, 186, 188, 187, 190, 192],
    [162, 168, 165, 172, 170, 175, 174, 178, 177, 181, 180, 184, 183, 188, 192],
    [120, 135, 128, 145, 140, 155, 150, 162, 158, 170, 165, 178, 174, 186, 192],
  ]
  return (
    <div className="w-full h-full flex flex-col" style={{ background: '#080C14', padding: '36px 14px 16px' }}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: '#1A2540' }}>
          <span style={{ fontSize: 11, color: '#00C896', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>A</span>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#F7F2E8', fontWeight: 600 }}>Apple Inc.</div>
          <div style={{ fontSize: 9, color: '#8899BB', fontFamily: 'var(--font-mono)' }}>AAPL · NASDAQ</div>
        </div>
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, color: '#F7F2E8' }}>$192.46</div>
      <div className="flex items-center gap-2 mb-4">
        <span style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)' }}>▲ +2.34 (1.23%)</span>
        <span style={{ fontSize: 9, color: '#8899BB' }}>Today</span>
      </div>
      {/* Tabs */}
      <div className="flex gap-1 mb-2 p-0.5 rounded-lg" style={{ background: '#0F1626' }}>
        {tabs.map((t, i) => (
          <div key={t} role="button" onClick={() => setTab(i)}
            aria-pressed={tab === i}
            className="flex-1 text-center py-1 rounded-md text-xs transition-all"
            style={{
              background: tab === i ? '#1A2540' : 'transparent',
              color: tab === i ? '#F7F2E8' : '#8899BB',
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
            }}>{t}</div>
        ))}
      </div>
      <Sparkline data={chartData[tab]} width={168} height={60} />
      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-2 mt-4">
        {[
          { l: 'Mkt Cap', v: '$2.97T' }, { l: 'P/E Ratio', v: '28.4x' },
          { l: '52W High', v: '$199.62' }, { l: '52W Low', v: '$143.90' },
        ].map(s => (
          <div key={s.l} className="rounded-lg p-2" style={{ background: '#0F1626' }}>
            <div style={{ fontSize: 8, color: '#8899BB' }}>{s.l}</div>
            <div style={{ fontSize: 11, color: '#F7F2E8', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{s.v}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-auto pt-4">
        <div className="flex-1 py-2.5 text-center rounded-xl text-xs font-medium" style={{ background: '#0F1626', color: '#FF6B7A', border: '1px solid rgba(255,107,122,0.3)' }}>Sell</div>
        <div className="flex-1 py-2.5 text-center rounded-xl text-xs font-medium" style={{ background: '#00C896', color: '#080C14' }}>Buy</div>
      </div>
    </div>
  )
}

// ── SCREEN: Transactions ───────────────────────────────────────────────────
function ScreenTransactions() {
  const txns = [
    { date: 'Today', items: [
      { name: 'Bought AAPL', amt: '-$820.00', type: 'buy', time: '10:32 AM' },
      { name: 'Dividend MSFT', amt: '+$42.60', type: 'dividend', time: '9:15 AM' },
    ]},
    { date: 'Yesterday', items: [
      { name: 'Deposit', amt: '+$2,000.00', type: 'deposit', time: '3:00 PM' },
      { name: 'Sold TSLA', amt: '+$1,240.00', type: 'sell', time: '11:44 AM' },
    ]},
    { date: 'Aug 1', items: [
      { name: 'Bought VTI', amt: '-$500.00', type: 'buy', time: '2:20 PM' },
    ]},
  ]
  const icons = { buy: '↓', sell: '↑', dividend: '✦', deposit: '+' }
  const colors = { buy: '#8899BB', sell: '#00C896', dividend: '#D4A847', deposit: '#00C896' }
  return (
    <div className="w-full h-full flex flex-col overflow-y-auto" style={{ background: '#080C14', padding: '36px 14px 16px' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: '#F7F2E8', marginBottom: 4 }}>Activity</div>
      <div style={{ fontSize: 9, color: '#8899BB', marginBottom: 16 }}>All transactions</div>
      {txns.map(group => (
        <div key={group.date} className="mb-4">
          <div style={{ fontSize: 8, color: '#8899BB', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 }}>{group.date}</div>
          {group.items.map((t, i) => (
            <div key={i} className="flex items-center gap-2.5 py-2.5" style={{ borderBottom: '1px solid #0F1626' }}>
              <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#0F1626', color: colors[t.type], fontSize: 11 }}>
                {icons[t.type]}
              </div>
              <div className="flex-1">
                <div style={{ fontSize: 10, color: '#F7F2E8' }}>{t.name}</div>
                <div style={{ fontSize: 8, color: '#8899BB', fontFamily: 'var(--font-mono)' }}>{t.time}</div>
              </div>
              <div style={{ fontSize: 11, color: t.amt.startsWith('+') ? '#00C896' : '#C2CEDF', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{t.amt}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

// ── SCREEN: Goals ──────────────────────────────────────────────────────────
function ScreenGoals() {
  const goals = [
    { name: 'Retirement', target: '$500,000', current: '$48,392', pct: 9.7, color: '#00C896', icon: '🏡' },
    { name: 'Emergency Fund', target: '$20,000', current: '$14,200', pct: 71, color: '#D4A847', icon: '🛡️' },
    { name: 'Travel Fund', target: '$8,000', current: '$2,100', pct: 26, color: '#8899BB', icon: '✈️' },
  ]
  return (
    <div className="w-full h-full flex flex-col overflow-y-auto" style={{ background: '#080C14', padding: '36px 14px 16px' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: '#F7F2E8', marginBottom: 4 }}>Goals</div>
      <div style={{ fontSize: 9, color: '#8899BB', marginBottom: 16 }}>Track your financial milestones</div>
      {goals.map(g => (
        <div key={g.name} className="rounded-xl p-3 mb-3" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
          <div className="flex items-center gap-2 mb-2">
            <span style={{ fontSize: 14 }}>{g.icon}</span>
            <div className="flex-1">
              <div style={{ fontSize: 11, color: '#F7F2E8', fontWeight: 500 }}>{g.name}</div>
              <div style={{ fontSize: 8, color: '#8899BB', fontFamily: 'var(--font-mono)' }}>{g.current} of {g.target}</div>
            </div>
            <div style={{ fontSize: 10, color: g.color, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{g.pct}%</div>
          </div>
          <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: '#1A2540' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${g.pct}%`, background: g.color }} />
          </div>
        </div>
      ))}
      <button className="w-full py-2.5 rounded-xl mt-2 text-xs" style={{ border: '1px dashed #1A2540', color: '#8899BB' }}>
        + Add new goal
      </button>
    </div>
  )
}

// ── SCREEN: Notifications / Alerts ────────────────────────────────────────
function ScreenAlerts() {
  const alerts = [
    { type: 'gain', icon: '📈', title: 'AAPL hit your target', body: 'Apple reached $192 — your set alert.', time: '2m ago', accent: '#00C896' },
    { type: 'rebalance', icon: '⚖️', title: 'Rebalance suggested', body: 'Your portfolio drifted 6% from target.', time: '1h ago', accent: '#D4A847' },
    { type: 'dividend', icon: '💰', title: 'Dividend received', body: 'MSFT paid $42.60 dividend.', time: '9h ago', accent: '#D4A847' },
    { type: 'news', icon: '📰', title: 'Fed rate decision', body: 'Rates held steady — market up 0.8%.', time: 'Yesterday', accent: '#8899BB' },
    { type: 'loss', icon: '📉', title: 'TSLA dropped 3.2%', body: 'Tesla fell below your floor alert.', time: 'Yesterday', accent: '#FF6B7A' },
  ]
  return (
    <div className="w-full h-full flex flex-col overflow-y-auto" style={{ background: '#080C14', padding: '36px 14px 16px' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: '#F7F2E8', marginBottom: 16 }}>Alerts</div>
      {alerts.map((a, i) => (
        <div key={i} className="flex gap-2.5 py-3" style={{ borderBottom: '1px solid #0F1626' }}>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-sm" style={{ background: `${a.accent}14` }}>
            {a.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 500 }}>{a.title}</div>
            <div style={{ fontSize: 9, color: '#8899BB', marginTop: 1 }}>{a.body}</div>
          </div>
          <div style={{ fontSize: 8, color: '#8899BB', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{a.time}</div>
        </div>
      ))}
    </div>
  )
}

// ── TABLET DASHBOARD ───────────────────────────────────────────────────────
function TabletDashboard() {
  const [activeNav, setActiveNav] = useState('Dashboard')
  const nav = ['Dashboard', 'Portfolio', 'Markets', 'Advisor', 'Settings']
  const allocations = [
    { name: 'US Equities', pct: 42, color: '#00C896', val: '$20,325' },
    { name: 'Intl Equities', pct: 18, color: '#D4A847', val: '$8,711' },
    { name: 'Fixed Income', pct: 22, color: '#8899BB', val: '$10,646' },
    { name: 'REITs', pct: 10, color: '#FF6B7A', val: '$4,839' },
    { name: 'Cash', pct: 8, color: '#1A2540', val: '$3,871' },
  ]
  const monthlyData = [28000, 30200, 29100, 32400, 31800, 34200, 33800, 36100, 38400, 40200, 42100, 48392]
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const maxVal = Math.max(...monthlyData)

  return (
    <div className="w-full rounded-3xl overflow-hidden" style={{ background: '#080C14', border: '1px solid #1A2540', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', maxWidth: 900, fontFamily: 'var(--font-body)' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3" style={{ background: '#0A0E1A', borderBottom: '1px solid #1A2540' }}>
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: '#00C896' }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 10L5 6L8 8L12 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: '#F7F2E8' }}>Vestly</span>
        </div>
        <nav className="flex items-center gap-1">
          {nav.map(n => (
            <div key={n} onClick={() => setActiveNav(n)}
              className="px-3 py-1.5 rounded-lg text-xs transition-all text-center cursor-pointer"
              style={{
                background: activeNav === n ? '#1A2540' : 'transparent',
                color: activeNav === n ? '#F7F2E8' : '#8899BB',
              }}>{n}</div>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: '#1A2540', fontSize: 9, color: '#00C896', fontFamily: 'var(--font-mono)' }}>AC</div>
        </div>
      </div>

      <div className="p-6 grid gap-4" style={{ gridTemplateColumns: '220px 1fr 200px' }}>
        {/* Left col */}
        <div className="flex flex-col gap-4">
          <div className="rounded-2xl p-4" style={{ background: 'linear-gradient(145deg, #0F1F3A, #0A1628)', border: '1px solid #1A2540' }}>
            <div style={{ fontSize: 9, color: '#8899BB', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Net Worth</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, color: '#F7F2E8', marginTop: 4 }}>$48,392</div>
            <div className="flex items-center gap-1.5 mt-1">
              <span style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)' }}>▲ 2.84%</span>
              <span style={{ fontSize: 9, color: '#8899BB' }}>30d</span>
            </div>
          </div>
          <div className="rounded-2xl p-4 flex flex-col gap-3" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
            <div style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 600 }}>Allocation</div>
            <div className="flex justify-center">
              <Donut segments={allocations.map(a => ({ pct: a.pct, color: a.color }))} />
            </div>
            {allocations.map(a => (
              <div key={a.name} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ background: a.color }} />
                  <span style={{ fontSize: 9, color: '#C2CEDF' }}>{a.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: 9, color: '#8899BB', fontFamily: 'var(--font-mono)' }}>{a.pct}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Center col */}
        <div className="flex flex-col gap-4">
          {/* Performance chart */}
          <div className="rounded-2xl p-4" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 600 }}>Portfolio Performance</div>
                <div style={{ fontSize: 9, color: '#8899BB' }}>12-month growth</div>
              </div>
              <div className="px-2 py-1 rounded-lg text-xs" style={{ background: 'rgba(0,200,150,0.1)', color: '#00C896', fontFamily: 'var(--font-mono)', fontSize: 9 }}>+72.4% YTD</div>
            </div>
            <div className="flex items-end gap-1" style={{ height: 100 }}>
              {monthlyData.map((v, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full rounded-t-sm transition-all" style={{
                    height: `${(v / maxVal) * 90}%`,
                    background: i === 11 ? '#00C896' : 'rgba(0,200,150,0.25)',
                    minHeight: 4,
                  }} />
                  <span style={{ fontSize: 7, color: '#8899BB', fontFamily: 'var(--font-mono)' }}>{months[i][0]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Holdings table */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #1A2540' }}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#0F1626', borderBottom: '1px solid #1A2540' }}>
              <span style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 600 }}>Holdings</span>
              <span style={{ fontSize: 9, color: '#00C896', fontFamily: 'var(--font-mono)', cursor: 'pointer' }}>View all →</span>
            </div>
            <div style={{ background: '#0A0E1A' }}>
              {[
                { name: 'Apple Inc.', ticker: 'AAPL', shares: 64, price: '$192.46', val: '$12,317', chg: '+1.23%', up: true, data: [180, 182, 181, 185, 183, 187, 186, 192] },
                { name: 'Microsoft', ticker: 'MSFT', shares: 28, price: '$350.20', val: '$9,806', chg: '+0.84%', up: true, data: [330, 335, 332, 340, 338, 345, 348, 350] },
                { name: 'Tesla', ticker: 'TSLA', shares: 16, price: '$256.40', val: '$4,102', chg: '-2.14%', up: false, data: [280, 275, 272, 268, 265, 260, 258, 256] },
                { name: 'NASDAQ ETF', ticker: 'QQQ', shares: 42, price: '$440.10', val: '$18,484', chg: '+0.62%', up: true, data: [420, 425, 422, 430, 428, 435, 438, 440] },
              ].map((h, i) => (
                <div key={i} className="grid px-4 py-2.5 items-center" style={{ gridTemplateColumns: '1fr 60px 60px 60px 60px', borderBottom: '1px solid #0F1626' }}>
                  <div>
                    <div style={{ fontSize: 10, color: '#F7F2E8' }}>{h.name}</div>
                    <div style={{ fontSize: 8, color: '#8899BB', fontFamily: 'var(--font-mono)' }}>{h.ticker} · {h.shares} shares</div>
                  </div>
                  <div style={{ fontSize: 9, color: '#C2CEDF', fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{h.price}</div>
                  <div style={{ fontSize: 9, color: '#C2CEDF', fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{h.val}</div>
                  <div style={{ fontSize: 9, color: h.up ? '#00C896' : '#FF6B7A', fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{h.chg}</div>
                  <div className="flex justify-end">
                    <Sparkline data={h.data} width={44} height={22} color={h.up ? '#00C896' : '#FF6B7A'} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right col */}
        <div className="flex flex-col gap-4">
          <div className="rounded-2xl p-4" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
            <div style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 600, marginBottom: 12 }}>AI Advisor</div>
            <div className="rounded-xl p-3 mb-3" style={{ background: 'rgba(0,200,150,0.08)', border: '1px solid rgba(0,200,150,0.2)' }}>
              <div style={{ fontSize: 9, color: '#C2CEDF', lineHeight: 1.6 }}>📊 Market volatility is low. Consider increasing equity exposure by 5%.</div>
            </div>
            <div className="rounded-xl p-3" style={{ background: 'rgba(212,168,71,0.08)', border: '1px solid rgba(212,168,71,0.2)' }}>
              <div style={{ fontSize: 9, color: '#C2CEDF', lineHeight: 1.6 }}>💰 You're on track to reach your retirement goal 2 years early.</div>
            </div>
          </div>
          <div className="rounded-2xl p-4" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
            <div style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 600, marginBottom: 10 }}>Quick Invest</div>
            <input className="w-full rounded-lg px-3 py-2 mb-2 text-xs outline-none" placeholder="Search assets..." style={{ background: '#1A2540', border: '1px solid #1A2540', color: '#C2CEDF', fontSize: 10 }} />
            <div className="flex gap-2 mb-3">
              {['$50', '$100', '$250', '$500'].map(v => (
                <div key={v} className="flex-1 py-1 text-center rounded-lg text-xs" style={{ background: '#1A2540', color: '#8899BB', fontSize: 8, fontFamily: 'var(--font-mono)' }}>{v}</div>
              ))}
            </div>
            <div className="w-full py-2 text-center rounded-xl text-xs font-medium" style={{ background: '#00C896', color: '#080C14' }}>Invest Now</div>
          </div>
          <div className="rounded-2xl p-4" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
            <div style={{ fontSize: 10, color: '#F7F2E8', fontWeight: 600, marginBottom: 8 }}>Goals Progress</div>
            {[
              { name: 'Retirement', pct: 9.7, color: '#00C896' },
              { name: 'Emergency', pct: 71, color: '#D4A847' },
              { name: 'Travel', pct: 26, color: '#8899BB' },
            ].map(g => (
              <div key={g.name} className="mb-2.5">
                <div className="flex justify-between mb-1">
                  <span style={{ fontSize: 9, color: '#C2CEDF' }}>{g.name}</span>
                  <span style={{ fontSize: 9, color: g.color, fontFamily: 'var(--font-mono)' }}>{g.pct}%</span>
                </div>
                <div className="w-full h-1 rounded-full overflow-hidden" style={{ background: '#1A2540' }}>
                  <div className="h-full rounded-full" style={{ width: `${g.pct}%`, background: g.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── STYLE GUIDE ────────────────────────────────────────────────────────────
function StyleGuide() {
  const colors = [
    { name: 'Navy', hex: '#080C14', role: 'Background' },
    { name: 'Navy Mid', hex: '#0F1626', role: 'Card Surface' },
    { name: 'Navy Light', hex: '#1A2540', role: 'Borders' },
    { name: 'Emerald', hex: '#00C896', role: 'Primary / Growth' },
    { name: 'Gold', hex: '#D4A847', role: 'Warning / Goals' },
    { name: 'Rose', hex: '#FF6B7A', role: 'Loss / Danger' },
    { name: 'Slate', hex: '#8899BB', role: 'Muted Text' },
    { name: 'Cream', hex: '#F7F2E8', role: 'Primary Text' },
  ]

  const typeScale = [
    { name: 'Display', font: 'Instrument Serif', size: '48px', weight: '400', sample: 'Smart Investing Made Simple', style: { fontFamily: 'var(--font-display)', fontSize: 32, color: '#F7F2E8' } },
    { name: 'Heading', font: 'Outfit', size: '24px', weight: '600', sample: 'Portfolio Dashboard', style: { fontFamily: 'var(--font-body)', fontSize: 20, fontWeight: 600, color: '#F7F2E8' } },
    { name: 'Body', font: 'Outfit', size: '14px', weight: '400', sample: 'Build wealth with intelligent, automated investing strategies.', style: { fontFamily: 'var(--font-body)', fontSize: 13, color: '#C2CEDF', lineHeight: 1.6 } },
    { name: 'Label', font: 'JetBrains Mono', size: '11px', weight: '500', sample: 'PORTFOLIO · RETURNS · 14.2%', style: { fontFamily: 'var(--font-mono)', fontSize: 10, color: '#8899BB', letterSpacing: '0.12em', textTransform: 'uppercase' } },
    { name: 'Data', font: 'JetBrains Mono', size: '28px', weight: '400', sample: '$48,392.40', style: { fontFamily: 'var(--font-display)', fontSize: 22, color: '#F7F2E8' } },
  ]

  const components = [
    {
      name: 'Primary Button',
      el: <div className="px-5 py-2.5 text-center rounded-xl text-sm font-medium transition-all hover:opacity-90" style={{ background: '#00C896', color: '#080C14', fontFamily: 'var(--font-body)' }}>Invest Now</div>
    },
    {
      name: 'Ghost Button',
      el: <div className="px-5 py-2.5 text-center rounded-xl text-sm font-medium transition-all" style={{ border: '1px solid #1A2540', color: '#C2CEDF', fontFamily: 'var(--font-body)' }}>View Details</div>
    },
    {
      name: 'Danger Button',
      el: <div className="px-5 py-2.5 text-center rounded-xl text-sm font-medium" style={{ border: '1px solid rgba(255,107,122,0.4)', color: '#FF6B7A', fontFamily: 'var(--font-body)' }}>Sell Position</div>
    },
    {
      name: 'Gain Badge',
      el: <span className="px-2.5 py-1 rounded-lg text-xs" style={{ background: 'rgba(0,200,150,0.12)', color: '#00C896', fontFamily: 'var(--font-mono)' }}>▲ +2.84%</span>
    },
    {
      name: 'Loss Badge',
      el: <span className="px-2.5 py-1 rounded-lg text-xs" style={{ background: 'rgba(255,107,122,0.12)', color: '#FF6B7A', fontFamily: 'var(--font-mono)' }}>▼ -1.23%</span>
    },
    {
      name: 'Input Field',
      el: <input className="px-3 py-2 rounded-lg outline-none text-sm" placeholder="Enter amount..." style={{ background: '#0F1626', border: '1px solid #1A2540', color: '#C2CEDF', fontFamily: 'var(--font-body)', width: 160 }} />
    },
  ]

  return (
    <div>
      {/* Colors */}
      <div className="mb-12">
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: '#F7F2E8', marginBottom: 6 }}>Color System</div>
        <div style={{ fontSize: 13, color: '#8899BB', marginBottom: 24 }}>A dark-first palette anchored in deep navy with a single high-trust accent.</div>
        <div className="grid grid-cols-4 gap-3" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {colors.map(c => (
            <div key={c.name}>
              <div className="w-full rounded-xl mb-2" style={{ height: 56, background: c.hex, border: c.hex === '#080C14' ? '1px solid #1A2540' : 'none' }} />
              <div style={{ fontSize: 11, color: '#F7F2E8', fontWeight: 500 }}>{c.name}</div>
              <div style={{ fontSize: 10, color: '#8899BB', fontFamily: 'var(--font-mono)' }}>{c.hex}</div>
              <div style={{ fontSize: 9, color: '#8899BB', marginTop: 1 }}>{c.role}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Typography */}
      <div className="mb-12">
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: '#F7F2E8', marginBottom: 6 }}>Typography</div>
        <div style={{ fontSize: 13, color: '#8899BB', marginBottom: 24 }}>Instrument Serif for trust-building display, Outfit for clarity, JetBrains Mono for data.</div>
        <div className="flex flex-col gap-6">
          {typeScale.map(t => (
            <div key={t.name} className="grid gap-4 items-baseline" style={{ gridTemplateColumns: '100px 1fr', borderBottom: '1px solid #0F1626', paddingBottom: 20 }}>
              <div>
                <div style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{t.name}</div>
                <div style={{ fontSize: 9, color: '#8899BB', marginTop: 2 }}>{t.font}</div>
                <div style={{ fontSize: 9, color: '#8899BB', fontFamily: 'var(--font-mono)' }}>{t.size} / {t.weight}</div>
              </div>
              <div style={t.style}>{t.sample}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Components */}
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: '#F7F2E8', marginBottom: 6 }}>Components</div>
        <div style={{ fontSize: 13, color: '#8899BB', marginBottom: 24 }}>Reusable UI elements with consistent rounding, color, and state treatment.</div>
        <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {components.map(c => (
            <div key={c.name} className="rounded-xl p-4" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
              <div className="flex items-center justify-center mb-4" style={{ minHeight: 48 }}>{c.el}</div>
              <div style={{ fontSize: 10, color: '#8899BB', textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{c.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── MAIN APP ───────────────────────────────────────────────────────────────
export default function App() {
  const [activeSection, setActiveSection] = useState('overview')

  const changeSection = (section) => {
    setActiveSection(section)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const screens = [
    { label: 'Splash', component: <ScreenSplash /> },
    { label: 'Onboarding 1', component: <ScreenOnboarding1 /> },
    { label: 'Risk Profile', component: <ScreenOnboarding2 /> },
    { label: 'Dashboard', component: <ScreenDashboard /> },
    { label: 'Recommendations', component: <ScreenRecommendations /> },
    { label: 'Asset Detail', component: <ScreenAssetDetail /> },
    { label: 'Transactions', component: <ScreenTransactions /> },
    { label: 'Goals', component: <ScreenGoals /> },
    { label: 'Alerts', component: <ScreenAlerts /> },
  ]

  const navItems = [
    { id: 'overview', label: 'Overview' },
    { id: 'mobile', label: 'Mobile Screens' },
    { id: 'tablet', label: 'Tablet Dashboard' },
    { id: 'style', label: 'Style Guide' },
  ]

  return (
    <div style={{ minHeight: '100vh', background: '#080C14', fontFamily: 'var(--font-body)' }}>
      {/* ── Portfolio Nav ── */}
      <header className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-8" style={{ background: 'rgba(8,12,20,0.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid #0F1626' }}>
        <div className="flex items-center gap-3">
          <Link to="/" aria-label="Return to portfolio" className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ color: '#C2CEDF', border: '1px solid #1A2540' }}>
            <ArrowLeft size={16} aria-hidden="true" />
          </Link>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#00C896' }}>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2 11L6 6L9 9L13 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: '#F7F2E8', lineHeight: 1 }}>Vestly</div>
            <div style={{ fontSize: 9, color: '#8899BB', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}>CASE STUDY</div>
          </div>
        </div>
        <nav aria-label="Vestly case study sections" className="order-3 flex w-full items-center gap-1 overflow-x-auto md:order-none md:w-auto">
          {navItems.map(n => (
            <div key={n.id} type="button" onClick={() => changeSection(n.id)}
              aria-current={activeSection === n.id ? 'page' : undefined}
              className="whitespace-nowrap px-4 py-2 rounded-lg text-xs transition-all"
              style={{
                background: activeSection === n.id ? '#1A2540' : 'transparent',
                color: activeSection === n.id ? '#F7F2E8' : '#8899BB',
              }}>{n.label}</div>
          ))}
        </nav>
        <div className="hidden items-center gap-2 sm:flex">
          <div className="text-xs px-2.5 py-1 rounded-lg" style={{ background: 'rgba(0,200,150,0.1)', color: '#00C896', fontFamily: 'var(--font-mono)', fontSize: 10 }}>2024</div>
          <div style={{ fontSize: 12, color: '#8899BB' }}>Fintech · Mobile + Tablet</div>
        </div>
      </header>

      <main id="case-study-content" tabIndex="-1">
        {/* ── OVERVIEW ── */}
        {activeSection === 'overview' && (
          <div>
            {/* Hero */}
            <div className="relative overflow-hidden" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
              {/* bg texture */}
              <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 60% 50%, rgba(0,200,150,0.06) 0%, transparent 70%)' }} />
              <div className="absolute right-0 top-0 bottom-0 w-1/2" style={{ background: 'linear-gradient(90deg, transparent, rgba(0,200,150,0.02))' }} />
              {/* grid lines */}
              <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(#00C896 1px, transparent 1px), linear-gradient(90deg, #00C896 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

              <div className="relative max-w-4xl px-5 py-20 md:px-16 md:py-24">
                <div className="flex items-center gap-2 mb-8">
                  <div className="h-px w-8" style={{ background: '#00C896' }} />
                  <span style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.15em', textTransform: 'uppercase' }}>UX/UI Case Study · Fintech</span>
                </div>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(40px, 6vw, 80px)', color: '#F7F2E8', lineHeight: 1.05, letterSpacing: '-1px', marginBottom: 24 }}>
                  Vestly —<br /><em style={{ color: '#00C896' }}>Smart Investing</em><br />Made Simple
                </h1>
                <p style={{ fontSize: 16, color: '#8899BB', lineHeight: 1.7, maxWidth: 480, marginBottom: 40 }}>
                  A wealth management app for millennials with robo-advisor features. Designing trust through clean typography and clear data presentation.
                </p>
                <div className="flex items-center gap-6">
                  <div type="button" onClick={() => changeSection('mobile')} className="px-6 py-3 rounded-xl text-sm font-medium transition-all hover:opacity-90" style={{ background: '#00C896', color: '#080C14' }}>
                    View Screens
                  </div>
                  <div type="button" onClick={() => changeSection('tablet')} className="px-6 py-3 rounded-xl text-sm font-medium" style={{ border: '1px solid #1A2540', color: '#44484c' }}>
                    Dashboard →
                  </div>
                </div>
              </div>
            </div>

            {/* Project Brief */}
            <div className="px-16 py-20" style={{ borderTop: '1px solid #0F1626' }}>
              <div className="grid gap-16" style={{ gridTemplateColumns: '1fr 1fr', maxWidth: 960 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>The Challenge</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: '#F7F2E8', lineHeight: 1.2, marginBottom: 16 }}>
                    Making wealth management <em>approachable</em>
                  </div>
                  <p style={{ fontSize: 14, color: '#8899BB', lineHeight: 1.8 }}>
                    Millennials face a trust gap with traditional finance. The challenge was to design an interface that feels sophisticated enough to handle real money, while being transparent and simple enough that first-time investors never feel lost.
                  </p>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>The Approach</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: '#F7F2E8', lineHeight: 1.2, marginBottom: 16 }}>
                    Data clarity as <em>design principle</em>
                  </div>
                  <p style={{ fontSize: 14, color: '#8899BB', lineHeight: 1.8 }}>
                    Every screen decision was filtered through one question: does this help users understand what their money is doing? Editorial typography builds authority; consistent data formatting builds confidence; progressive disclosure prevents overwhelm.
                  </p>
                </div>
              </div>
            </div>

            {/* Deliverables */}
            <div className="px-16 py-16" style={{ background: '#0A0E1A', borderTop: '1px solid #0F1626' }}>
              <div style={{ maxWidth: 960 }}>
                <div style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 32 }}>Deliverables</div>
                <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                  {[
                    { num: '09', label: 'Mobile Screens', sub: 'Onboarding → Dashboard' },
                    { num: '01', label: 'Tablet Dashboard', sub: 'Full desktop view' },
                    { num: '03', label: 'Core Flows', sub: 'Invest · Review · Goals' },
                    { num: '01', label: 'Style Guide', sub: 'Colors · Type · Components' },
                  ].map(d => (
                    <div key={d.label} className="rounded-2xl p-5" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 36, color: '#00C896', lineHeight: 1 }}>{d.num}</div>
                      <div style={{ fontSize: 12, color: '#F7F2E8', fontWeight: 600, marginTop: 8 }}>{d.label}</div>
                      <div style={{ fontSize: 10, color: '#8899BB', marginTop: 2 }}>{d.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Design principles */}
            <div className="px-16 py-20" style={{ borderTop: '1px solid #0F1626', maxWidth: 960 }}>
              <div style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 32 }}>Design Principles</div>
              <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                {[
                  { num: '01', title: 'Trust through Clarity', body: 'Numbers are always formatted consistently. Gains and losses use unambiguous color and directional icons. No hidden fees or confusing jargon.' },
                  { num: '02', title: 'Progressive Disclosure', body: 'New users see only what they need. Advanced controls like tax-loss harvesting emerge only when the user is ready — never on first launch.' },
                  { num: '03', title: 'Calm Confidence', body: 'Even in volatile markets, the UI stays composed. No flashing alerts or panic-inducing red screens — information is presented factually, not emotionally.' },
                ].map(p => (
                  <div key={p.num} className="rounded-2xl p-6" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#00C896', marginBottom: 12 }}>{p.num}</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: '#F7F2E8', marginBottom: 10 }}>{p.title}</div>
                    <p style={{ fontSize: 13, color: '#8899BB', lineHeight: 1.7 }}>{p.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── MOBILE SCREENS ── */}
        {activeSection === 'mobile' && (
          <div className="px-5 py-12 md:px-16 md:py-16">
            <div className="mb-12">
              <div style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>Mobile App · iOS</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 40, color: '#F7F2E8', lineHeight: 1.1 }}>9 Screens, <em>one coherent system</em></div>
            </div>
            <div className="overflow-x-auto pb-8" style={{ scrollbarWidth: 'none' }}>
              <div className="flex gap-8" style={{ width: 'max-content' }}>
                {screens.map((s, i) => (
                  <PhoneFrame key={i} label={s.label}>
                    {s.component}
                  </PhoneFrame>
                ))}
              </div>
            </div>
            {/* Screen annotations */}
            <div className="mt-16 grid gap-4" style={{ gridTemplateColumns: 'repeat(3, 1fr)', maxWidth: 960 }}>
              {[
                { title: 'Onboarding Flow', screens: '01–03', body: 'Three-step onboarding collects risk tolerance and investment goals without overwhelming new users.' },
                { title: 'Core Dashboard', screens: '04–06', body: 'Portfolio overview, AI-curated recommendations, and asset drill-down with interactive charting.' },
                { title: 'Activity & Goals', screens: '07–09', body: 'Transaction history, goal progress tracking, and a real-time alert system for market events.' },
              ].map(a => (
                <div key={a.title} className="rounded-xl p-5" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <div style={{ fontSize: 9, color: '#00C896', fontFamily: 'var(--font-mono)' }}>{a.screens}</div>
                    <div className="h-px flex-1" style={{ background: '#1A2540' }} />
                  </div>
                  <div style={{ fontSize: 14, color: '#F7F2E8', fontWeight: 600, marginBottom: 6 }}>{a.title}</div>
                  <p style={{ fontSize: 12, color: '#8899BB', lineHeight: 1.6 }}>{a.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── TABLET DASHBOARD ── */}
        {activeSection === 'tablet' && (
          <div className="px-5 py-12 md:px-16 md:py-16">
            <div className="mb-12">
              <div style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>Tablet · Dashboard View</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 40, color: '#F7F2E8', lineHeight: 1.1, marginBottom: 8 }}>The full picture, <em>at a glance</em></div>
              <p style={{ fontSize: 14, color: '#8899BB', maxWidth: 560 }}>The tablet dashboard surfaces all portfolio data simultaneously — allocation, performance, holdings, and AI recommendations — in a three-column layout designed for information density without cognitive overload.</p>
            </div>
            <TabletDashboard />
            <div className="mt-12 grid gap-4" style={{ gridTemplateColumns: 'repeat(3, 1fr)', maxWidth: 900 }}>
              {[
                { title: 'Three-column layout', body: 'Summary left, performance center, quick actions right. Information flows from macro to micro.' },
                { title: 'Live AI Advisor', body: 'Robo-advisor suggestions surface contextually based on market conditions and portfolio drift.' },
                { title: 'Unified nav bar', body: 'Top navigation keeps the user oriented across Dashboard, Markets, Advisor, and Settings.' },
              ].map(f => (
                <div key={f.title} className="rounded-xl p-5" style={{ background: '#0F1626', border: '1px solid #1A2540' }}>
                  <div style={{ fontSize: 13, color: '#F7F2E8', fontWeight: 600, marginBottom: 6 }}>{f.title}</div>
                  <p style={{ fontSize: 12, color: '#8899BB', lineHeight: 1.6 }}>{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── STYLE GUIDE ── */}
        {activeSection === 'style' && (
          <div className="px-5 py-12 md:px-16 md:py-16" style={{ maxWidth: 960 }}>
            <div className="mb-16">
              <div style={{ fontSize: 10, color: '#00C896', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>Design System</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 40, color: '#F7F2E8', lineHeight: 1.1 }}>Style Guide &amp; <em>Design Tokens</em></div>
            </div>
            <StyleGuide />
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-16 flex flex-col items-center justify-between gap-4 px-5 py-10 text-center md:flex-row md:px-16 md:text-left" style={{ borderTop: '1px solid #0F1626' }}>
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: '#00C896' }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 10L5 6L8 8L11 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 14, color: '#F7F2E8' }}>Vestly</span>
        </div>
        <div style={{ fontSize: 11, color: '#8899BB', fontFamily: 'var(--font-mono)' }}>Fintech · UX/UI Case Study · 2024</div>
        <Link to="/" style={{ fontSize: 11, color: '#00C896' }}>Back to portfolio</Link>
      </footer>
    </div>
  )
}
